<script lang="ts">
	import { getContext, onDestroy, onMount } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { format } from 'svelte-i18n';
	import { Button, ButtonV2 } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import MainStep from 'src/components/Devices/DeviceWizard/AddEditSteps/MainStep.svelte';
	import { devices } from 'src/stores/lists/devices';
	import { performDeviceEditSave } from 'src/helpers/edit_device_save_helper';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	let editDevice: Device = { ...device };
	let requestLoader = false;

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	$: if (device && editDevice?.id !== device?.id) {
		editDevice = { ...device };
	}

	const onDetailsSaved =
		getContext<(payload: { roomChanged: boolean; roomId?: number; shared?: boolean }) => void>(
			'onDetailsSaved',
		);

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const onSave = async () => {
		if (!editDevice?.name || editDevice.name.trim() === '') return;
		requestLoader = true;
		registerStickySave();
		try {
			const storeDevice = $devices[editDevice.id];
			const toSave: Device = {
				...editDevice,
				name: editDevice.name.trim(),
				name_sync: storeDevice?.name_sync ?? editDevice.name_sync,
				name_sync_target: storeDevice?.name_sync_target ?? editDevice.name_sync_target,
			};
			await performDeviceEditSave(toSave);
			devices.update(toSave);
			editDevice = { ...toSave };
			onDetailsSaved?.({
				roomChanged: false,
			});
		} finally {
			requestLoader = false;
			registerStickySave();
		}
	};

	$: isSaveDisabled = !editDevice?.name || editDevice.name.trim() === '' || requestLoader;

	const registerStickySave = () => {
		if (!stickyBottomAction) return;
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: onSave,
				loading: requestLoader,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	};

	// Re-register whenever save state or layout mode changes
	$: if (stickyBottomAction) {
		$showNewLayout;
		isSaveDisabled;
		requestLoader;
		registerStickySave();
	}

	onMount(() => {
		registerStickySave();
	});

	onDestroy(() => {
		// Only clear if we still own the sticky action (avoid wiping the next tab's Save)
		stickyBottomAction?.update((current) =>
			current?.onClick === onSave ? null : (current ?? null),
		);
	});
</script>

<MainStep bind:editDevice />

{#if !$showNewLayout}
	<div class="mt-l">
		<svelte:component
			this={ButtonComp}
			class="width-sm-full"
			variant="primary"
			size="m"
			responsiveSize={{ sm: 'm' }}
			loading={requestLoader}
			disabled={isSaveDisabled}
			icon="fas fa-save"
			on:click={onSave}
			aria-label={$format('button_save', { default: 'Save' })}
		>
			{$format('button_save', { default: 'Save' })}
		</svelte:component>
	</div>
{/if}
